import { useState } from "react";
import { Settings, Edit2, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DeviceStateStore } from "@production-hub/shared";
import {
  createDefaultStore,
  storeToVideoHubState,
} from "@production-hub/shared";
import type { ConnectionType } from "./connectionTypes";
import { CONNECTION_TYPE_LABELS } from "./connectionTypes";
import { VideoHubPanel } from "./VideoHubPanel";

interface DeviceStateEditorProps {
  connectionType: ConnectionType;
  connectionId: string;
  store: DeviceStateStore | undefined;
  onSetStore: (connectionId: string, store: DeviceStateStore) => void;
  onUpdateValue: (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => void;
}

export const DeviceStateEditor = ({
  connectionType,
  connectionId,
  store,
  onSetStore,
  onUpdateValue,
}: DeviceStateEditorProps) => {
  if (!store) {
    return (
      <InitializeForm
        connectionType={connectionType}
        connectionId={connectionId}
        onSetStore={onSetStore}
      />
    );
  }

  if (connectionType === "videohub") {
    return (
      <VideoHubEditor
        connectionId={connectionId}
        store={store}
        onSetStore={onSetStore}
        onUpdateValue={onUpdateValue}
      />
    );
  }

  return (
    <GenericEditor
      connectionType={connectionType}
      store={store}
      connectionId={connectionId}
      onUpdateValue={onUpdateValue}
    />
  );
};

// ─── Initialize form ─────────────────────────────────────────

interface InitializeFormProps {
  connectionType: ConnectionType;
  connectionId: string;
  onSetStore: (connectionId: string, store: DeviceStateStore) => void;
}

const INIT_FIELDS: Record<
  ConnectionType,
  { label: string; key: string; defaultValue: number }[]
> = {
  videohub: [
    { label: "Input Count", key: "inputCount", defaultValue: 20 },
    { label: "Output Count", key: "outputCount", defaultValue: 20 },
  ],
  atem: [{ label: "Input Count", key: "inputCount", defaultValue: 20 }],
  behringer_wing: [
    { label: "Channel Count", key: "channelCount", defaultValue: 32 },
    { label: "Bus Count", key: "busCount", defaultValue: 16 },
  ],
  dlive: [
    { label: "Channel Count", key: "channelCount", defaultValue: 32 },
    { label: "Scene Count", key: "sceneCount", defaultValue: 8 },
  ],
  companion: [
    { label: "Page Count", key: "pageCount", defaultValue: 10 },
    { label: "Buttons per Page", key: "buttonsPerPage", defaultValue: 32 },
  ],
  // vMix state is read live from its HTTP API, so there is nothing to seed.
  vmix: [],
  generic: [],
};

const InitializeForm = ({
  connectionType,
  connectionId,
  onSetStore,
}: InitializeFormProps) => {
  const fields = INIT_FIELDS[connectionType] ?? [];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, String(f.defaultValue)])),
  );
  const [model, setModel] = useState(
    CONNECTION_TYPE_LABELS[connectionType] ?? "",
  );

  const handleInitialize = () => {
    const options: Record<string, number | string> = {};
    for (const field of fields) {
      options[field.key] = Number(values[field.key]) || field.defaultValue;
    }
    if (connectionType === "videohub" || connectionType === "atem") {
      options.model = model;
    }
    onSetStore(connectionId, createDefaultStore(connectionType, options));
  };

  return (
    <Card data-testid="device-state-init">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Initialize Device State
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {(connectionType === "videohub" || connectionType === "atem") && (
          <div>
            <Label>Model Name</Label>
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              data-testid="device-model-input"
            />
          </div>
        )}
        {fields.map((field) => (
          <div key={field.key}>
            <Label>{field.label}</Label>
            <Input
              type="number"
              value={values[field.key]}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
              data-testid={`device-${field.key}-input`}
            />
          </div>
        ))}
        <Button
          onClick={handleInitialize}
          className="w-full"
          data-testid="device-init-button"
        >
          Initialize
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── VideoHub editor (reuses VideoHubPanel) ──────────────────

interface VideoHubEditorProps {
  connectionId: string;
  store: DeviceStateStore;
  onSetStore: (connectionId: string, store: DeviceStateStore) => void;
  onUpdateValue: (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => void;
}

const VideoHubEditor = ({
  connectionId,
  store,
  onSetStore,
  onUpdateValue,
}: VideoHubEditorProps) => {
  const state = storeToVideoHubState(store);

  const handleRoute = (output: number, input: number) => {
    onUpdateValue(connectionId, `/outputs/${output}/source`, input);
  };

  const handleLabelInput = (index: number, label: string) => {
    onUpdateValue(connectionId, `/inputs/${index}/label`, label);
  };

  const handleLabelOutput = (index: number, label: string) => {
    onUpdateValue(connectionId, `/outputs/${index}/label`, label);
  };

  return (
    <div data-testid="device-state-editor">
      <VideoHubPanel
        state={state}
        onRoute={handleRoute}
        onLabelInput={handleLabelInput}
        onLabelOutput={handleLabelOutput}
      />
      <div className="mt-3 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onSetStore(connectionId, createDefaultStore("videohub"))
          }
          data-testid="device-state-reset"
        >
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
};

// ─── Generic key-value editor ────────────────────────────────

interface GenericEditorProps {
  connectionType: ConnectionType;
  store: DeviceStateStore;
  connectionId: string;
  onUpdateValue: (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => void;
}

const GenericEditor = ({
  connectionType,
  store,
  connectionId,
  onUpdateValue,
}: GenericEditorProps) => {
  // Group paths by first segment
  const groups = new Map<string, [string, string | number | boolean][]>();
  const sortedKeys = Object.keys(store.values).sort();
  for (const key of sortedKeys) {
    const segments = key.split("/").filter(Boolean);
    const group = segments[0] ?? "other";
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push([key, store.values[key]]);
  }

  return (
    <div className="space-y-4" data-testid="device-state-editor">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="gap-1">
          <Settings className="h-3 w-3" />
          {CONNECTION_TYPE_LABELS[connectionType]}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {sortedKeys.length} values
        </span>
      </div>

      {[...groups.entries()].map(([group, entries]) => (
        <Card key={group} data-testid={`device-group-${group}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm capitalize">{group}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Path</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(([path, value]) => (
                  <GenericValueRow
                    key={path}
                    path={path}
                    value={value}
                    onSave={(v) => onUpdateValue(connectionId, path, v)}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

interface GenericValueRowProps {
  path: string;
  value: string | number | boolean;
  onSave: (value: string | number | boolean) => void;
}

const GenericValueRow = ({ path, value, onSave }: GenericValueRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));

  const handleSave = () => {
    const original = value;
    let parsed: string | number | boolean;
    if (typeof original === "boolean") {
      parsed = editValue.toLowerCase() === "true";
    } else if (typeof original === "number") {
      parsed = Number(editValue);
      if (isNaN(parsed)) parsed = 0;
    } else {
      parsed = editValue;
    }
    if (parsed !== original) {
      onSave(parsed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(String(value));
    setIsEditing(false);
  };

  // Display shortened path (remove group prefix)
  const shortPath = path.replace(/^\/[^/]+/, "");

  return (
    <TableRow data-testid={`value-row-${path}`}>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {shortPath || path}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
            className="h-7 text-sm"
            autoFocus
          />
        ) : (
          <span className="text-sm">
            {typeof value === "boolean" ? (
              <Badge
                variant={value ? "default" : "secondary"}
                className="text-xs"
              >
                {String(value)}
              </Badge>
            ) : (
              String(value)
            )}
          </span>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex gap-1">
            <Button variant="ghost" size="icon-sm" onClick={handleSave}>
              <Check className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={handleCancel}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsEditing(true)}
          >
            <Edit2 className="h-3 w-3" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};
